/**
 * Gated HITL bridge for `create_ring` (the second mutating path).
 *
 * Flow: parse the model's tool input → `resolveRing` → `previewRing` → a
 * {@link RingProposal} the card renders. The shared gate (≤1-pending preamble,
 * resolve-failure mapping, Apply/Reject wiring) lives in
 * {@link mutatingToolRunner}; this file holds only what's specific to a ring —
 * the two signed offsets, the grid-rounding, the locked outer>inner invariant
 * (enforced in the safety core), and the proposal's display fields.
 * Pure/React-free.
 */
import {
    resolveRing,
    previewRing,
    commitRing,
    type RingDeps,
} from './pipeline';
import {
    asRecord,
    failedPlan,
    gateProposal,
    mutatingPreamble,
    normalizeDistanceInName,
    quantizeCmTracked,
    resolveFailureToPlan,
    type MutatingPlan,
    type MutatingToolCall,
    type MutationOutcome,
    type PendingFlag,
} from '../core';
import type { CreateRingInput } from '../../contract';

export type RingToolCall = MutatingToolCall;

export interface RingPlanDeps extends RingDeps, PendingFlag {}

export interface RingProposal {
    readonly toolCallId: string;
    readonly target: {
        readonly roiNumber: number;
        readonly name: string;
        readonly currentVolumeCm3: number;
    };
    /** Canonical, grid-rounded signed cm offsets (inner < outer). */
    readonly innerCm: number;
    readonly outerCm: number;
    /** Original offsets, set only when finer than the grid (disclosure). */
    readonly requestedInnerCm?: number;
    readonly requestedOuterCm?: number;
    readonly newRoi: string;
    /** Previewed volume of the ring itself (cm³). */
    readonly ringVolumeCm3: number;
    /** Ring minus source volume (cm³) — for model chaining. */
    readonly volumeDeltaCm3: number;
    /** Commit the ALREADY-previewed geometry into a new structure. */
    apply(): Promise<MutationOutcome>;
    /** The clinician declined; the model is told and does not assume success. */
    reject(): MutationOutcome;
    showPreview(): void;
    hidePreview(): void;
}

export type RingPlan = MutatingPlan<RingProposal>;

/** Defensively read the model-supplied (untyped) tool input. */
function readInput(input: unknown): CreateRingInput | undefined {
    const o = asRecord(input);
    if (o === undefined) {
        return undefined;
    }
    if (
        typeof o.roi !== 'string' ||
        typeof o.innerCm !== 'number' ||
        typeof o.outerCm !== 'number' ||
        typeof o.newRoi !== 'string'
    ) {
        return undefined;
    }
    return {
        roi: o.roi,
        innerCm: o.innerCm,
        outerCm: o.outerCm,
        newRoi: o.newRoi,
    };
}

export async function planRingToolCall(
    call: RingToolCall,
    deps: RingPlanDeps
): Promise<RingPlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }

    const parsed = readInput(call.input);
    if (parsed === undefined) {
        return failedPlan(call.toolCallId, 'invalid create_ring input');
    }

    // Round both offsets to the clinical grid (0.1 mm) — sub-grid precision is
    // below the achievable voxel resolution. The grid values are what get
    // previewed, applied, and embedded in the name, so display/geometry/name
    // cannot diverge. Finer originals are kept so the card discloses the
    // rounding. The name token is normalized from the OUTER offset (the
    // conventional ring dimension); the explicit inner→outer line on the card
    // remains the authoritative display of both.
    const { cm: innerCm, requestedCm: requestedInnerCm } = quantizeCmTracked(
        parsed.innerCm
    );
    const { cm: outerCm, requestedCm: requestedOuterCm } = quantizeCmTracked(
        parsed.outerCm
    );
    const newRoi = normalizeDistanceInName(parsed.newRoi, outerCm);

    const resolved = resolveRing(
        { roi: parsed.roi, innerCm, outerCm, newRoi },
        deps
    );
    if (resolved.kind !== 'validated') {
        return resolveFailureToPlan(resolved, call.toolCallId);
    }

    const preview = await previewRing(resolved.validated, deps);
    if (preview.kind === 'failed') {
        return failedPlan(call.toolCallId, preview.reason);
    }

    const previewed = preview.previewed;
    const proposal: RingProposal = {
        toolCallId: call.toolCallId,
        target: {
            roiNumber: resolved.validated.sourceRoiNumber,
            name: parsed.roi,
            currentVolumeCm3: resolved.validated.sourceVolumeCm3,
        },
        innerCm,
        outerCm,
        ...(requestedInnerCm !== undefined ? { requestedInnerCm } : {}),
        ...(requestedOuterCm !== undefined ? { requestedOuterCm } : {}),
        newRoi,
        ringVolumeCm3: previewed.computed.resultVolumeCm3,
        volumeDeltaCm3: previewed.volumeDeltaCm3,
        ...gateProposal(previewed.computed, deps, () =>
            commitRing(previewed, deps)
        ),
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
